import React, { useState } from 'react';
import './LoadListModal.scss';
import Modal from 'react-bootstrap/Modal';
import { useStoreContext } from '../App/StoreProvider';
import { CustomButton } from '../_common';
import ModalHeaderTitle from './ModalHeaderTitle';
import LoadItem from './LoadListItem';
import { Loading } from '../_common';
import { LoadWithDriverItem } from '../../store/types/AddLoadModalState';
import * as Actions from '../../store/actions/addLoadModal';
import { useMutation } from '@apollo/client/react';
import { DELETE_LOADS } from '../../graphql/client/mutations';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';

interface LoadListModalProps {
    jobId: number | null | undefined;
    load?: string;
    onClickNext: () => void;
    onClickHide: () => void;
    onClickBack: () => void;
    onClickSave: () => void;
    show: boolean;
    proppantName: string | undefined;
    lofa: string | undefined;
    purchaseOrder: string | undefined;
    region?: {
        regionName: string | null;
        id: number | null;
        extSystemName: string | null;
        extSystemReference: string | null;
        regionCode: string | null;
    } | null;
    mutationLoading?: any;
    handleHideWhenRemoveAllCreatedLoads?: () => void;
    disabled?: boolean;
}

interface SelectedLoad {
    loadId: number | null;
}
const LoadListModal = ({
    jobId,
    proppantName,
    lofa,
    purchaseOrder,
    show,
    onClickNext,
    onClickHide,
    onClickBack,
    onClickSave,
    mutationLoading,
    handleHideWhenRemoveAllCreatedLoads,
    disabled,
}: LoadListModalProps): React.ReactElement => {
    const isRemoveLoadsEnabled = useAuthContext().userHasPermission(PermissionEnum.DeleteLoad);
    const [state, dispatch] = useStoreContext();
    const loadIds: any = [];
    const currentLoadIds: any = [];
    const initialArray: SelectedLoad[] = [];
    let hasTmsValues = false;
    let hasErrorMessage = false;
    const [selectedLoads, setSelectedLoads] = useState(initialArray);
    const {
        addLoadModal: { loadsWithDrivers },
    } = state;
    const [isDeleted, setIsDeleted] = useState(false);

    const onLoadItemCheck = (checkedStatus: boolean, loadId: number): void => {
        let result: SelectedLoad[] = [];
        if (checkedStatus === true) {
            result = selectedLoads.concat({ loadId: loadId });
        } else if (checkedStatus === false) {
            result = selectedLoads.filter((item): boolean => item.loadId !== loadId);
        }

        setSelectedLoads(result);
    };

    const onCheckAll = (checkedStatus: boolean, loadsList: []): void => {
        const result: SelectedLoad[] = [];
        if (checkedStatus === true) {
            loadsList.forEach((loadId: number): void => {
                result.push({ loadId: loadId });
            });
        }
        setSelectedLoads(result);
    };

    selectedLoads.forEach(it => {
        loadIds.push(it.loadId);
    });

    const isLoadChecked = (loadId: number): boolean => {
        return selectedLoads.filter((item): boolean => item.loadId === loadId)[0] !== undefined;
    };

    const [deleteLoad] = useMutation(DELETE_LOADS, {
        onCompleted() {
            dispatch(Actions.removeLoadIds(loadIds));
            setSelectedLoads(initialArray);
            setIsDeleted(true);
        },
    });

    const deleteLoadIds = (): void => {
        if (!disabled) {
            const variables = { input: { loadIds } };
            deleteLoad({ variables });
        }
    };

    const onClickAdd = async (loadId: number): Promise<void> => {
        await dispatch(Actions.setCurrentLoadId(loadId));
        await dispatch(Actions.setErrorMessageForLoad(loadId, ''));
        onClickNext();
    };

    const removeDriverFromLoad = (loadId: number): void => {
        dispatch(Actions.removeDriverFromLoad(loadId));
    };

    if (loadsWithDrivers.length < 1 && mutationLoading) {
        return <Loading />;
    }
    if (loadsWithDrivers.length < 1 && handleHideWhenRemoveAllCreatedLoads && isDeleted) {
        handleHideWhenRemoveAllCreatedLoads();
    }
    if (loadsWithDrivers) {
        loadsWithDrivers.forEach((driver, index) => {
            driver.tmsId = state.addLoadModal.tmsIds[index];
            currentLoadIds.push(driver.id);
        });

        hasTmsValues = !loadsWithDrivers.every(load => !load.tmsId);
        hasErrorMessage = !loadsWithDrivers.every(load => !load.errorMessage);
    }

    const callHide = (): void => {
        if (!disabled) {
            onClickHide();
        }
    };

    const callBack = (): void => {
        if (!disabled) {
            onClickBack();
        }
    };

    return (
        <Modal show={show} onHide={callHide} scrollable size="lg" backdrop="static">
            <Modal.Header className="LoadListModal-Header">
                <Modal.Title className="LoadListModal-Header-Container">
                    <ModalHeaderTitle
                        title="Add Loads"
                        onCancelClick={callHide}
                        hasBackButton
                        backButtonTitle="Create Loads"
                        onBackClick={callBack}
                        proppantName={proppantName}
                        lofa={lofa}
                        load={'0'}
                        sandPO={purchaseOrder}
                        disabled={disabled}
                    ></ModalHeaderTitle>
                    <div className="LoadListModal-Table-Title-Container">
                        <div className="LoadListModal-Checkbox-Container">
                            <input
                                type="checkbox"
                                className="LoadListModal-Checkbox"
                                onChange={(event): void => onCheckAll(event.currentTarget.checked, currentLoadIds)}
                            />
                        </div>
                        <div className="LoadListModal-Item">Load #</div>
                        {loadsWithDrivers && hasTmsValues && <div className="LoadListModal-Item">TMS ID</div>}
                        <div className="LoadListModal-Item">Driver Name</div>
                        {loadsWithDrivers && hasErrorMessage && <div className="LoadListModal-Item"></div>}
                        {disabled && <div className="LoadListModal-Item"></div>}
                        <div
                            className={`LoadListModal-Item ${
                                isRemoveLoadsEnabled ? '' : 'LoadManagementHeader-DeleteButton-Disabled'
                            }`}
                            onClick={deleteLoadIds}
                        >
                            <i className="material-icons RemoveLoads-DeleteIcon">highlight_off</i>
                            Remove Loads
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="LoadListModal-Body">
                <div className="LoadListModal-LoadList-Container">
                    {loadsWithDrivers &&
                        loadsWithDrivers.map(
                            (load: LoadWithDriverItem): React.ReactElement => (
                                <LoadItem
                                    key={load!.id}
                                    load={load}
                                    hasTmsValues={hasTmsValues}
                                    isChecked={isLoadChecked(load!.id)}
                                    onClickAdd={() => onClickAdd(load.id)}
                                    onChange={(checkedStatus: boolean): void =>
                                        onLoadItemCheck(checkedStatus, load!.id)
                                    }
                                    onClickRemoveDriver={() => removeDriverFromLoad(load.id)}
                                    errorMessage={load.errorMessage}
                                    isDisabled={disabled}
                                ></LoadItem>
                            ),
                        )}
                </div>
            </Modal.Body>
            <Modal.Footer className="LoadListModal-Footer">
                <CustomButton title="Save" isDisabled={disabled} onClick={onClickSave}></CustomButton>
            </Modal.Footer>
        </Modal>
    );
};

export default LoadListModal;

